import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import ComponentRenderer from 'aldoo-ra/aldoo-vbecs/components/ComponentRenderer.jsx';
import { getThemeCssVariables, calculateRect, getAnchorStyle, getNodeChildren, measureNode, getBreakpointForWidth } from 'aldoo-ra/aldoo-vbecs/utils.js';
import { DESIGN_W, DESIGN_H } from 'aldoo-ra/aldoo-vbecs/constants.js';
import { getBehavior } from 'aldoo-ra/aldoo-vbecs/components/behaviors/registry.js';
import { resolveNode } from 'aldoo-ra/aldoo-vbecs/utils/responsive.js';
import { getCurrentTheme, onThemeChange } from 'aldoo-ra/aldoo-vbecs/utils/theme-helper.js';

const NodeItem = React.memo(({
    node: rawNode, activeNodes, prefabs, rootW, rootH, activeBreakpoint, useCSSLayout
}) => {
    // Resolve node for responsive overrides (still needed for non-layout props like variant)
    const node = resolveNode(rawNode, activeBreakpoint);

    // Check if disabled - always hide disabled nodes in live mode
    const isEnabled = node.props?.enabled !== false;
    if (!isEnabled) return null;

    // Visibility Check - in live mode, visibility prop is ignored (items are always visible unless disabled)
    // The visibility prop only affects edit mode in the editor
    // With CSS Layout, visibility is handled by CSS (display: none).
    // But we still return null here to prevent React rendering unnecessary DOM?
    // If we return null, the DOM node is missing.
    // CSS Selector `#vbecs-{id}` will match nothing. Correct.
    // But if CSS handles visibility, we should let CSS do it to avoid hydration mismatch?
    // If server renders it (visible default) but hides via CSS.
    // If JS returns null, hydration mismatch (server has node, client has null).
    // So if useCSSLayout is true, we should RENDER it but let CSS hide it.
    // UNLESS server also returns null?
    // Node resolution runs on server too.
    // So server sees 'xs', returns null.
    // Client sees 'xs', returns null. Matches.
    // So returning null is fine.

    const children = getNodeChildren(activeNodes, node.id);

    const rawParent = activeNodes.find(n => n.id === node.parentId);
    const parent = rawParent ? resolveNode(rawParent, activeBreakpoint) : null;

    let parentBehaviorDef;
    if (parent && parent.attachedComponents) {
        for (const ac of parent.attachedComponents) {
                const def = getBehavior(ac.type);
                if (def && def.isLayoutProvider) {
                    parentBehaviorDef = def;
                    break;
                }
        }
    }

    // Resolve visual dimensions for children of this node
    // In runtime, we assume full width/height relative to parent container if root
    const visualRect = calculateRect(node, rootW, rootH);
    const isLayoutManaged = !!parentBehaviorDef;

    // Layout Style Logic
    let style = {};
    if (!useCSSLayout) {
        style = getAnchorStyle(node, rootW, rootH, isLayoutManaged);
    }

    if (parentBehaviorDef && parentBehaviorDef.getChildStyle && parent) {
        const ac = parent.attachedComponents?.find(c => c.type === parentBehaviorDef.id);
        if (ac) {
            const childStyles = parentBehaviorDef.getChildStyle(ac.props, node.props, node);
            Object.assign(style, childStyles);
        }
    }

    // Pass styles and props directly to ComponentRenderer to avoid double-nesting divs
    // This matches the behavior in the Editor's NodeItem
    return (
        <ComponentRenderer
            node={node}
            prefabs={prefabs}
            isLiveMode={true}
            activeBreakpoint={activeBreakpoint}
            useCSSLayout={useCSSLayout}
            allNodes={activeNodes}
            wrapperStyle={style}
            wrapperClassName={`pointer-events-auto vbecs-node-${node.id}`}
            wrapperProps={{ id: `vbecs-${node.id}` }}
        >
            {children.map(child => (
                <NodeItem
                    key={child.id}
                    node={child}
                    activeNodes={activeNodes}
                    prefabs={prefabs}
                    rootW={visualRect.width}
                    rootH={visualRect.height}
                    activeBreakpoint={activeBreakpoint}
                    useCSSLayout={useCSSLayout}
                />
            ))}
        </ComponentRenderer>
    );
});

const VBECSRenderer = ({ nodes, prefabs, initialViewportWidth, generatedLayoutCSS, ssrThemeData }) => {
  // Initialize with SSR theme data if available (fixes hydration mismatch)
  // On server: ssrThemeData from pageContext, getCurrentTheme() returns null
  // On client first render: use ssrThemeData to match server, then update with client theme
  const [themeVars, setThemeVars] = useState(() => {
    let theme = getCurrentTheme();

    // If SSR theme data is provided, extract the appropriate active theme
    if (ssrThemeData && !theme) {
      // Determine context (admin vs site) - default to site for VBECS pages
      const isAdminPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
      theme = isAdminPage ? ssrThemeData.adminActiveTheme : ssrThemeData.siteActiveTheme;
    }

    const vars = getThemeCssVariables(theme);
    // Ensure we never return a falsy value that could serialize to "null"
    return vars || {};
  });

  useEffect(() => {
      const unsubscribe = onThemeChange((theme) => {
          setThemeVars(getThemeCssVariables(theme));
      });
      return unsubscribe;
  }, []);

  // Filter for root nodes (no parent)
  const rootNodes = getNodeChildren(nodes, undefined);
  const containerRef = useRef(null);
  const useCSSLayout = !!generatedLayoutCSS;

  if (typeof window !== 'undefined' && generatedLayoutCSS) {
      console.log('VBECSRenderer: Using CSS Layout. CSS Length:', generatedLayoutCSS.length);
  }

  // Initialize with detected viewport from SSR or Design default
  // If CSS Layout is used, we don't strictly need accurate dimensions for layout,
  // but we still need them for `activeBreakpoint` calculation for variants (colors/fonts)

  // CRITICAL: To avoid hydration mismatch, server and client MUST use same initial value
  // Priority: 1) initialViewportWidth from cookie (SSR), 2) window.innerWidth (client), 3) DESIGN_W default
  // Get actual window width immediately on client before first render
  const getInitialWidth = () => {
      if (typeof window !== 'undefined') {
          return window.innerWidth;
      }
      return initialViewportWidth || DESIGN_W;
  };

  const [dimensions, setDimensions] = useState({
      width: getInitialWidth(),
      height: DESIGN_H
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Only update if dimensions actually changed to avoid loop/flicker
        setDimensions(prev => {
            if (prev.width === entry.contentRect.width && prev.height === entry.contentRect.height) return prev;

            // Save width to cookie for SSR optimization on next load
            if (typeof document !== 'undefined') {
                document.cookie = `viewportWidth=${Math.round(entry.contentRect.width)}; path=/; max-age=31536000`;
            }

            return {
                width: entry.contentRect.width,
                height: entry.contentRect.height
            };
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Calculate breakpoint using shared utility
  const activeBreakpoint = getBreakpointForWidth(dimensions.width);

  return (
    <div className="w-full h-screen relative overflow-auto bg-admin_canvas" style={themeVars}>
        {generatedLayoutCSS && (
            <style dangerouslySetInnerHTML={{ __html: generatedLayoutCSS }} />
        )}
        <div ref={containerRef} className="absolute inset-0 w-full h-full min-h-screen">
            {rootNodes.map(node => (
                <NodeItem
                    key={node.id}
                    node={node}
                    activeNodes={nodes}
                    prefabs={prefabs}
                    rootW={dimensions.width}
                    rootH={dimensions.height}
                    activeBreakpoint={activeBreakpoint}
                    useCSSLayout={useCSSLayout}
                />
            ))}
        </div>
    </div>
  );
};
export default VBECSRenderer;